import { useState, useCallback, useContext } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DeleteMachineStatusDialog } from "components/CaseManagement/CaseDetail/MachineStatus/widgets/DeleteMachineStatusDialog";
import { useFetchDelete } from "hooks/fetch/useFetchDelete";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { TableCell, TableRow, Link, IconButton, Tooltip } from "@mui/material";
import { HistoryMachineStatusDialog } from "./widgets/HistoryMachineStatusDialog";
import { UserAvatar } from "components/Avatar/UserAvatar";
import { withTransientProps } from "core/utils/transientProps";

export const MachineStatusStaticRow = ({
  loading,
  caseId,
  isEditable,
  setEditMode,
  machineStatus,
  onHandleOpenInNewTab,
  machineStatusRefetch,
  history,
  userInfo = false
}) => {
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const {
    loading: deleteLoading,
    deleteCallback: deleteMachineStatusLog
  } = useFetchDelete(
    `machine_status/${machineStatus.id}`,
    () => {
      addSnackbarToQueue({
        message: "Machine status log deleted.",
        severity: "success"
      });
      machineStatusRefetch();
    },
    () => {
      addSnackbarToQueue({
        message: "Machine status log failed to delete.",
        severity: "error"
      });
    }
  );

  const [deleteDialog, setDeleteDialog] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const handleEdit = useCallback(() => {
    setEditMode("patch");
  }, [setEditMode]);

  const handleDelete = useCallback(() => {
    setDeleteDialog(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    setDeleteDialog(false);
    deleteMachineStatusLog();
  }, [deleteMachineStatusLog]);

  const handleShowHistory = useCallback(() => {
    setShowHistory(true);
  }, []);

  return (
    // ** Column list **
    // Status
    // Date set
    // Expected RTS
    // Case ID
    // Updated
    // Updated By
    // Actions (With invisible header)
    <StyledTableRow $isEditable={isEditable} hover>
      <TableCell data-col={1} component="th" scope="row">
        {machineStatus?.displayStatus || "Unknown"}
      </TableCell>
      <TableCell data-col={2}>
        {machineStatus?.displayDateActioned || "Not set"}
      </TableCell>
      <TableCell data-col={3}>
        {userInfo && (machineStatus?.displayRts || "Not set")}
        {!userInfo && (
          <Tooltip
            title="Click to see the RTS Change Log"
            placement="bottom-start"
            aria-label="RTS Change Log"
          >
            <Link
              component="button"
              variant="body2"
              onClick={() => handleShowHistory()}
            >
              {machineStatus?.displayRts}
              {history.length > 1 && ` (${history.length})`}
            </Link>
          </Tooltip>
        )}
      </TableCell>
      {!userInfo && (
        <TableCell data-col={4}>
          {machineStatus?.displayId === "-" ? (
            machineStatus?.displayId
          ) : machineStatus.case !== caseId ? (
            <Link
              component="button"
              variant="body2"
              onClick={() => onHandleOpenInNewTab(machineStatus.case)}
            >
              {machineStatus?.displayId}
            </Link>
          ) : (
            <span>{machineStatus?.displayId}</span>
          )}
        </TableCell>
      )}
      {userInfo && (
        <>
          <TableCell data-col={5} component="th" scope="row">
            {machineStatus?.change_date}
          </TableCell>
          <TableCell data-col={6} component="th" scope="row">
            <UserAvatar
              name={machineStatus?.change_user_full_name}
              toolTip={machineStatus?.change_user_full_name}
              size={100}
            />
          </TableCell>
        </>
      )}
      <TableCell data-col={7}>
        {isEditable && (
          <>
            <IconButton
              onClick={handleEdit}
              title="Edit Expected RTS"
              disabled={machineStatus.status === 1 || loading || deleteLoading}
              color="secondary"
              size="large"
            >
              <EditIcon fontSize="small" />
            </IconButton>

            <IconButton
              onClick={handleDelete}
              title="Delete Machine Status"
              color="secondary"
              disabled={loading || deleteLoading}
              size="large"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>

            <DeleteMachineStatusDialog
              dialog={deleteDialog}
              setDialog={setDeleteDialog}
              onConfirmDelete={handleConfirmDelete}
            />
          </>
        )}
        <HistoryMachineStatusDialog
          caseId={caseId}
          onHandleOpenInNewTab={onHandleOpenInNewTab}
          openDialog={showHistory}
          onHideDialog={setShowHistory}
          history={history}
        />
      </TableCell>
    </StyledTableRow>
  );
};

const StyledTableRow = styled(TableRow, withTransientProps)`
  th,
  td {
    color: ${({ $isEditable }) => `${$isEditable ? "black" : "#616161"}`};
    border-bottom: ${({ $isEditable }) =>
      `${$isEditable ? "1px solid #C4C4C4" : "none"}`};
  }
`;

MachineStatusStaticRow.propTypes = {
  loading: PropTypes.bool,
  caseId: PropTypes.number,
  isEditable: PropTypes.bool,
  setEditMode: PropTypes.func,
  machineStatus: PropTypes.object,
  onHandleOpenInNewTab: PropTypes.func,
  machineStatusRefetch: PropTypes.func,
  history: PropTypes.array,
  userInfo: PropTypes.bool
};
